import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { TabItem } from '../BottomTabs';

interface BadgeManagement {
  tabBadges: Record<string, number | string>;
  updateTabBadge: (tabId: string, badge: number | string | undefined) => void;
  clearTabBadge: (tabId: string) => void;
  clearAllBadges: () => void;
  getTabsWithBadges: (tabs: TabItem[], enableBadges: boolean) => TabItem[];
}

/**
 * Custom hook for managing tab badges
 * Handles badge updates, clearing, and automatic clearing when tabs are visited
 */
export const useTabBadges = (
  tabs: TabItem[], 
  enableTabBarBadges: boolean = true,
  autoClearDelay: number = 1000
): BadgeManagement => {
  const location = useLocation();
  const [tabBadges, setTabBadges] = useState<Record<string, number | string>>({});

  // Badge management functions
  const updateTabBadge = (tabId: string, badge: number | string | undefined) => {
    setTabBadges(prev => ({
      ...prev,
      [tabId]: badge || 0
    }));
  };

  const clearTabBadge = (tabId: string) => {
    setTabBadges(prev => {
      const updated = { ...prev };
      delete updated[tabId];
      return updated;
    });
  };

  const clearAllBadges = () => {
    setTabBadges({});
  };

  // Clear badge when tab is visited
  useEffect(() => {
    if (!enableTabBarBadges || autoClearDelay <= 0) return;

    const currentTab = tabs.find(tab => 
      location.pathname === tab.route || location.pathname.startsWith(tab.route + '/')
    );
    
    if (currentTab && tabBadges[currentTab.id]) {
      // Clear badge after a short delay to show user acknowledgment
      const timer = setTimeout(() => {
        clearTabBadge(currentTab.id);
      }, autoClearDelay);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, tabs, tabBadges, enableTabBarBadges, autoClearDelay]);

  // Get tabs with current badge state
  const getTabsWithBadges = (inputTabs: TabItem[], enableBadges: boolean): TabItem[] => {
    return inputTabs.map(tab => ({
      ...tab,
      badge: enableBadges ? tabBadges[tab.id] : undefined
    }));
  };

  return {
    tabBadges,
    updateTabBadge,
    clearTabBadge,
    clearAllBadges,
    getTabsWithBadges
  };
};